import { InboxEntryComment } from '@this+that/models';

import {
  CommentsEntryState,
  HandlerSet,
  InboxEntryCommentsState,
} from './types';

export const INITIAL_ENTRY_STATE: CommentsEntryState = {
  comments: [],
  exclusiveStartKey: null,
  hasMore: false,
  isLoading: false,
  isLoadingMore: false,
  error: null,
  editingCommentId: null,
  creatingComment: false,
  updatingCommentId: null,
  deletingCommentId: null,
};

const updateEntry = (
  set: HandlerSet,
  inboxEntryId: string,
  updater: (entry: CommentsEntryState) => Partial<CommentsEntryState>,
) => {
  set((state: InboxEntryCommentsState) => {
    const current = state.stateByEntry[inboxEntryId] ?? {
      ...INITIAL_ENTRY_STATE,
    };
    return {
      stateByEntry: {
        ...state.stateByEntry,
        [inboxEntryId]: {
          ...current,
          ...updater(current),
        },
      },
    };
  });
};

export const handleInitEntry = (set: HandlerSet, inboxEntryId: string) => {
  set((state: InboxEntryCommentsState) => {
    if (state.stateByEntry[inboxEntryId]) {
      return state;
    }
    return {
      stateByEntry: {
        ...state.stateByEntry,
        [inboxEntryId]: { ...INITIAL_ENTRY_STATE },
      },
    };
  });
};

export const handleClearEntry = (set: HandlerSet, inboxEntryId: string) => {
  set((state: InboxEntryCommentsState) => {
    const { [inboxEntryId]: _, ...rest } = state.stateByEntry;
    return { stateByEntry: rest };
  });
};

export const handleSetComments = (
  set: HandlerSet,
  inboxEntryId: string,
  comments: InboxEntryComment[],
  exclusiveStartKey: string | null,
) => {
  updateEntry(set, inboxEntryId, () => ({
    comments,
    exclusiveStartKey,
    hasMore: exclusiveStartKey !== null,
    isLoading: false,
    error: null,
  }));
};

export const handleAppendComments = (
  set: HandlerSet,
  inboxEntryId: string,
  comments: InboxEntryComment[],
  exclusiveStartKey: string | null,
) => {
  updateEntry(set, inboxEntryId, (current) => {
    const existingIds = new Set(current.comments.map((c) => c.id));
    const newComments = comments.filter((c) => !existingIds.has(c.id));
    return {
      comments: [...current.comments, ...newComments],
      exclusiveStartKey,
      hasMore: exclusiveStartKey !== null,
      isLoadingMore: false,
    };
  });
};

export const handleAddComment = (
  set: HandlerSet,
  inboxEntryId: string,
  comment: InboxEntryComment,
) => {
  updateEntry(set, inboxEntryId, (current) => ({
    comments: [comment, ...current.comments],
  }));
};

export const handleUpdateComment = (
  set: HandlerSet,
  inboxEntryId: string,
  comment: InboxEntryComment,
) => {
  updateEntry(set, inboxEntryId, (current) => ({
    comments: current.comments.map((c) => (c.id === comment.id ? comment : c)),
  }));
};

export const handleRemoveComment = (
  set: HandlerSet,
  inboxEntryId: string,
  commentId: string,
) => {
  updateEntry(set, inboxEntryId, (current) => ({
    comments: current.comments.filter((c) => c.id !== commentId),
  }));
};

export const handleReplaceComment = (
  set: HandlerSet,
  inboxEntryId: string,
  tempId: string,
  comment: InboxEntryComment,
) => {
  updateEntry(set, inboxEntryId, (current) => ({
    comments: current.comments.map((c) => (c.id === tempId ? comment : c)),
  }));
};

export const handleSetLoading = (
  set: HandlerSet,
  inboxEntryId: string,
  isLoading: boolean,
) => {
  updateEntry(set, inboxEntryId, () => ({ isLoading }));
};

export const handleSetLoadingMore = (
  set: HandlerSet,
  inboxEntryId: string,
  isLoadingMore: boolean,
) => {
  updateEntry(set, inboxEntryId, () => ({ isLoadingMore }));
};

export const handleSetError = (
  set: HandlerSet,
  inboxEntryId: string,
  error: string | null,
) => {
  updateEntry(set, inboxEntryId, () => ({ error }));
};

export const handleSetEditingCommentId = (
  set: HandlerSet,
  inboxEntryId: string,
  commentId: string | null,
) => {
  updateEntry(set, inboxEntryId, () => ({ editingCommentId: commentId }));
};

export const handleSetCreatingComment = (
  set: HandlerSet,
  inboxEntryId: string,
  creating: boolean,
) => {
  updateEntry(set, inboxEntryId, () => ({ creatingComment: creating }));
};

export const handleSetUpdatingCommentId = (
  set: HandlerSet,
  inboxEntryId: string,
  commentId: string | null,
) => {
  updateEntry(set, inboxEntryId, () => ({ updatingCommentId: commentId }));
};

export const handleSetDeletingCommentId = (
  set: HandlerSet,
  inboxEntryId: string,
  commentId: string | null,
) => {
  updateEntry(set, inboxEntryId, () => ({ deletingCommentId: commentId }));
};
